import { Card, Heading, IconButton, LuIcon, Separator, Text, toast } from "@overmap-ai/blocks"
import {
	selectAttachmentsOfFormSubmissionFieldValues,
	selectFormById,
	selectFormIdentifiersOfForm,
	selectFormRevisionById,
	selectFormSubmissionById,
	selectUserById,
} from "@overmap-ai/core"
import { useAbortableEffect } from "@overmap-ai/hooks"
import { gray } from "@radix-ui/colors"
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router"
import { allSettled } from "better-all"
import { cx } from "class-variance-authority"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { FormSubmissionDropdownMenu } from "../../components/DropdownMenus/FormSubmissionDropdownMenu"
import { FormSubmitter } from "../../components/Form/FormSubmitter"
import {
	useDeserializeFieldSchema,
	useDeserializeFieldValues,
	useHydrateFieldValuesWithAttachments,
} from "../../components/Form/hooks"
import { FormIdentifiersList } from "../../components/FormIdentifiersList/FormIdentifiersList"
import { FormSubmissionTimeline } from "../../components/FormSubmissionTimeline/FormSubmissionTimeline"
import { LuIconWithWrapper } from "../../components/Icons/LuIconWithWrapper"
import { UserIcon } from "../../components/Icons/UserIcon"
import { FormSubmissionIndexLabel } from "../../components/IndexLabels/FormSubmissionIndexLabel"
import { DEBUG } from "../../constants"
import { interactiveTextCva } from "../../cva"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectLatestFormSubmissionFieldValuesOfFormSubmission } from "../../store/selectors/forms"
import { formatTimestampDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/forms/submissions/$formSubmissionId/")

export const FormSubmissionDetailView = memo(() => {
	const { formSubmissionId } = routeApi.useParams()

	const formSubmission = useAppSelector(selectFormSubmissionById(formSubmissionId))

	if (!formSubmission) {
		throw new Error(`forms submission with id ${formSubmissionId} not found in store.`)
	}

	const form = useAppSelector(selectFormById(formSubmission.form))

	if (!form) {
		throw new Error(`form with uuid ${formSubmission.uuid}, not found in store.`)
	}

	const formRevision = useAppSelector(selectFormRevisionById(formSubmission.form_revision))

	if (!formRevision) {
		throw new Error(`forms revision with id ${formSubmission.form_revision} not found in store.`)
	}

	const submittedBy = useAppSelector(selectUserById(formSubmission.created_by ?? -1))
	const formIdentifiers = useShallowEqaulSelector(selectFormIdentifiersOfForm(formRevision.form))
	const latestFormSubmissionFieldValues = useAppSelector(
		selectLatestFormSubmissionFieldValuesOfFormSubmission(formSubmission.uuid),
	)
	const attachments = useShallowEqaulSelector(
		selectAttachmentsOfFormSubmissionFieldValues(latestFormSubmissionFieldValues?.uuid ?? ""),
	)
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const navigate = useNavigate()

	const fieldSchema = useDeserializeFieldSchema(formRevision.fields)

	const fieldValues = useDeserializeFieldValues(fieldSchema, latestFormSubmissionFieldValues?.values)

	const valuesWithAttachments = useHydrateFieldValuesWithAttachments(
		fieldSchema,
		fieldValues,
		attachments,
		sdk.formSubmissionFieldValuesAttachments,
	)

	const handleEdit = useCallback(() => {
		void navigate({
			from: "/$organizationId/forms/submissions/$formSubmissionId/",
			to: "/$organizationId/forms/submissions/$formSubmissionId/edit",
		})
	}, [navigate])

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete form submission",
			description: "Are you sure you want to delete this form submission? This cannot be undone.",
			onAction: async () => {
				await navigate({
					replace: true,
					from: "/$organizationId/forms/submissions/$formSubmissionId/",
					to: "/$organizationId/forms/$formId",
					params: (prev) => ({
						...prev,
						formId: formRevision.form,
					}),
				})

				sdk.formSubmissions.delete(formSubmission.uuid).catch(() => {
					toast.error({
						title: "Failed to delete form submission",
						description: "Please try again later.",
					})
				})
			},
		})
	}, [openConfirmDeleteDialog, navigate, formRevision.form, formSubmission.uuid])

	useAbortableEffect(
		(abortController) => {
			const fetchTimelineData = async () => {
				await allSettled(
					{
						async formIdentifierValues() {
							return sdk.formIdentifierValues.refreshStoreForFormSubmission(
								formSubmission.uuid,
								this.$signal,
							)
						},
						async formSubmissionFieldValues() {
							return sdk.formSubmissionFieldValues.refreshStoreForFormSubmission(
								formSubmission.uuid,
								this.$signal,
							)
						},
						async formSubmissionFieldValuesAttachments() {
							await this.$.formSubmissionFieldValues
							return sdk.formSubmissionFieldValuesAttachments.refreshStoreForFormSubmission(
								formSubmission.uuid,
								this.$signal,
							)
						},
					},
					{ debug: DEBUG, signal: abortController.signal },
				)
			}

			void fetchTimelineData()
		},
		[formSubmission.uuid],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex justify-between gap-2">
					<div className="flex min-w-0 items-center gap-2">
						<LuIconWithWrapper icon={modelIconMapping.form} color={gray.gray9} background />
						<Heading size="xl" className="truncate">
							{form.name}
						</Heading>
						<FormSubmissionIndexLabel formSubmissionId={formSubmission.uuid} size="xl" accentColor="base" />
					</div>
					<FormSubmissionDropdownMenu
						formSubmission={formSubmission}
						onEdit={handleEdit}
						onDelete={handleDelete}
						size="sm"
						align="end"
					>
						<IconButton size="sm" variant="ghost" accentColor="base">
							<LuIcon icon={modelIconMapping.more} />
						</IconButton>
					</FormSubmissionDropdownMenu>
				</div>

				<Separator size="full" />

				<div className="flex flex-col gap-4 overflow-y-auto overflow-x-hidden">
					<Card className="flex grow flex-col gap-4" size="sm">
						<div className="flex flex-col gap-1">
							<Text accentColor="base" size="sm">
								Submitted by
							</Text>
							<Text size="sm">
								<UserIcon userId={submittedBy?.id} size="xs" className="align-middle" />
								&nbsp;
								<Text>{getUserDisplayLabel(submittedBy)}</Text>
							</Text>
						</div>

						<div className="flex flex-col gap-1">
							<Text accentColor="base" size="sm">
								Submitted at
							</Text>
							<Text size="sm">{formatTimestampDate(formSubmission.submitted_at)}</Text>
						</div>

						<div className="flex flex-col gap-1">
							<Text accentColor="base" size="sm">
								Linked to
							</Text>
							<Text accentColor="base" size="sm">
								{formSubmission.asset ? (
									<>
										<LuIconWithWrapper
											icon={modelIconMapping.assetType}
											background
											color={gray.gray9}
											className="align-sub"
										/>
										&nbsp;
										<Link
											className={cx("min-w-0", interactiveTextCva())}
											from="/$organizationId/forms/submissions/$formSubmissionId/"
											to="/$organizationId/$projectId/assets/$assetId"
											params={(prev) => ({
												...prev,
												projectId: formSubmission.project!,
												assetId: formSubmission.asset!,
											})}
										>
											<Text className="truncate">Asset</Text>
										</Link>
									</>
								) : null}

								{formSubmission.issue ? (
									<>
										<LuIconWithWrapper
											icon={modelIconMapping.issue}
											background
											color={gray.gray9}
											className="align-sub"
										/>
										&nbsp;
										<Link
											className={cx("min-w-0", interactiveTextCva())}
											from="/$organizationId/forms/submissions/$formSubmissionId/"
											to="/$organizationId/$projectId/issues/$issueId"
											params={(prev) => ({
												...prev,
												projectId: formSubmission.project!,
												issueId: formSubmission.issue!,
											})}
										>
											<Text className="truncate">Issue</Text>
										</Link>
									</>
								) : null}

								{!formSubmission.issue && !formSubmission.asset ? (
									<Text className="truncate" highContrast>
										None
									</Text>
								) : null}
							</Text>
						</div>
					</Card>

					{formIdentifiers.length > 0 && (
						<Card className="flex flex-col gap-2" size="sm">
							<Heading size="sm">Identifiers</Heading>
							<FormIdentifiersList formSubmission={formSubmission} formIdentifiers={formIdentifiers} />
						</Card>
					)}

					<Card size="sm">
						<FormSubmitter formRevision={formRevision} values={valuesWithAttachments} disabled size="sm" />
					</Card>

					<Separator size="full" />

					<FormSubmissionTimeline formSubmission={formSubmission} size="sm" />
				</div>
			</section>
		</ViewContainer>
	)
})
FormSubmissionDetailView.displayName = "FormSubmissionDetailView"
